<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps({
  parentIcon: {
    type: Array,
    default: () => ['fas', 'gear']
  },
  parentLabel: {
    type: String,
    required: true
  },
  children: {
    type: Array,
    required: true
  },
  isExpanded: {
    type: Boolean,
    default: false
  },
  parentBadge: {
    type: Boolean,
    default: false
  },
  // Collapsed mode: open the dropdown on hover instead of requiring a click
  hoverOpen: {
    type: Boolean,
    default: false
  },
  // Clicking the parent row navigates to the first routed child (the chevron
  // still toggles the accordion in expanded mode)
  parentNavigates: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const router = useRouter()
const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})
const menuAlignBottom = ref(false)

// Child routes may carry a query string ('/flow/integrations?add=1') — matching
// is done on the path portion only, so query-only variants never steal active
// state from their plain sibling.
function routePath(childRoute) {
  return childRoute.split('?')[0]
}

function matchesRoute(childRoute) {
  if (!childRoute) return false
  const path = routePath(childRoute)
  return route.path === path || route.path.startsWith(path + '/')
}

// Find the active child — most-specific (longest) path prefix match wins;
// on a tie the first-listed child keeps it.
const activeChild = computed(() => {
  const matches = props.children.filter(c => matchesRoute(c.route))
  if (!matches.length) return undefined
  return matches.reduce((best, c) => routePath(c.route).length > routePath(best.route).length ? c : best)
})

function handleChildClick(child) {
  if (typeof child.action === 'function') {
    child.action()
  }
  close()
}

// Determine which icon to display (active child's icon or parent icon)
const displayIcon = computed(() => {
  // Only replace icon in collapsed mode
  if (!props.isExpanded && activeChild.value) {
    return activeChild.value.icon
  }
  return props.parentIcon
})

// Check if any child is active
const hasActiveChild = computed(() => {
  return !!activeChild.value
})

// Check if a specific child route is active — only the most-specific sibling wins
function isChildActive(childRoute) {
  return activeChild.value?.route === childRoute
}

function toggleOpen() {
  if (props.isExpanded) {
    // Expanded mode: toggle inline list
    isOpen.value = !isOpen.value
  } else {
    // Collapsed mode: toggle dropdown
    isOpen.value = !isOpen.value
  }
}

function handleParentClick() {
  if (props.parentNavigates) {
    const first = props.children.find(c => c.route)
    if (first) {
      if (typeof first.action === 'function') {
        first.action()
      }
      router.push(first.route)
      if (props.isExpanded) {
        isOpen.value = true
      } else {
        close()
      }
      return
    }
  }
  toggleOpen()
}

function close() {
  isOpen.value = false
}

// Hover-open behavior (collapsed mode only). The dropdown is teleported and
// sits a gap away from the rail, so closing is delayed and cancelled when the
// pointer reaches the menu.
let hoverCloseTimer = null

function cancelScheduledClose() {
  clearTimeout(hoverCloseTimer)
}

function scheduleClose() {
  if (!props.hoverOpen || props.isExpanded) return
  cancelScheduledClose()
  hoverCloseTimer = setTimeout(close, 150)
}

function handleTriggerEnter() {
  if (!props.hoverOpen || props.isExpanded) return
  cancelScheduledClose()
  isOpen.value = true
}

// Position the dropdown menu
function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value || props.isExpanded) return

  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = 220
  const viewportHeight = window.innerHeight
  const spacing = 16 // Space between dropdown and sidebar

  // Calculate available space above and below
  const spaceBelow = viewportHeight - rect.bottom
  const spaceAbove = rect.top

  // Position to the RIGHT of the sidebar with spacing
  let left = rect.right + spacing

  // Boundary check
  const viewportWidth = window.innerWidth
  if (left + menuWidth > viewportWidth - 10) {
    left = viewportWidth - menuWidth - 10
  }

  // Determine vertical alignment based on available space
  // Since admin tools are at bottom, prefer aligning bottom of menu with bottom of button
  // The gap bridge (::before) spans only the trigger button's row, so it needs
  // the button height and which menu edge the button sits at.
  let top
  if (spaceBelow < 300 && spaceAbove > spaceBelow) {
    // Align bottom of menu with bottom of button
    top = rect.bottom
    menuAlignBottom.value = true
    menuStyle.value = {
      position: 'fixed',
      bottom: `${viewportHeight - top}px`,
      left: `${left}px`,
      minWidth: `${menuWidth}px`,
      '--sidebar-bridge-height': `${rect.height}px`
    }
  } else {
    // Align top of menu with top of button
    top = rect.top
    menuAlignBottom.value = false
    menuStyle.value = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${menuWidth}px`,
      '--sidebar-bridge-height': `${rect.height}px`
    }
  }
}

// Close when clicking outside (only for collapsed dropdown)
function handleClickOutside(event) {
  // Only apply click-outside logic when in collapsed mode with dropdown open
  if (props.isExpanded) return

  if (triggerRef.value && triggerRef.value.contains(event.target)) {
    return
  }
  if (menuRef.value && menuRef.value.contains(event.target)) {
    return
  }
  close()
}

// Close on escape (only for collapsed dropdown)
function handleKeydown(event) {
  if (event.key === 'Escape' && !props.isExpanded && isOpen.value) {
    close()
  }
}

// Update position when opened
watch(isOpen, async (newVal) => {
  if (newVal && !props.isExpanded) {
    await nextTick()
    updateMenuPosition()
  }
})

// Close dropdown when sidebar expands/collapses
watch(() => props.isExpanded, () => {
  close()
})

// Close when navigating somewhere this group doesn't own; groups whose child
// matches the new route stay open.
watch(() => route.path, () => {
  if (isOpen.value && !activeChild.value) {
    close()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  clearTimeout(hoverCloseTimer)
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})
</script>

<template>
  <li
    class="nav-item-wrapper expandable-wrapper"
    @mouseenter="handleTriggerEnter"
    @mouseleave="scheduleClose"
  >
    <!-- Parent item -->
    <div
      ref="triggerRef"
      class="sidebar-nav"
      :class="{ 'active': hasActiveChild || (isOpen && !isExpanded) }"
      @click="handleParentClick"
    >
      <span class="icon-wrap">
        <font-awesome-icon class="icon" :icon="displayIcon" />
        <span v-if="parentBadge && !isExpanded" class="parent-badge"></span>
      </span>
      <span v-if="isExpanded" class="nav-text">{{ parentLabel }}</span>
      <span v-if="parentBadge && isExpanded" class="parent-badge inline"></span>
      <font-awesome-icon
        v-if="isExpanded"
        class="chevron"
        :icon="['fas', isOpen ? 'chevron-up' : 'chevron-down']"
        @click.stop="toggleOpen"
      />
    </div>

    <!-- Dropdown menu for collapsed sidebar (teleported) -->
    <Teleport to="body">
      <Transition name="expandable-dropdown">
        <div
          v-if="isOpen && !isExpanded"
          ref="menuRef"
          class="expandable-dropdown-menu"
          :class="{ 'align-bottom': menuAlignBottom }"
          :style="menuStyle"
          @mouseenter="cancelScheduledClose"
          @mouseleave="scheduleClose"
        >
          <div class="dropdown-header">{{ parentLabel }}</div>
          <div class="dropdown-items">
            <template v-for="child in children" :key="child.route || child.label">
              <router-link
                v-if="child.route"
                :to="child.route"
                class="dropdown-item"
                :class="[{ 'active': isChildActive(child.route), 'locked': child.lock }, child.variant ? `variant-${child.variant}` : '']"
                :data-guide="child.guide"
                @click="handleChildClick(child)"
              >
                <span class="item-icon-wrap">
                  <font-awesome-icon class="item-icon" :icon="child.icon" />
                  <span v-if="child.badge" class="child-badge"></span>
                  <font-awesome-icon v-if="child.lock" class="child-lock-icon" :icon="['fas', 'lock']" />
                </span>
                <span>{{ child.label }}</span>
              </router-link>
              <div
                v-else
                class="dropdown-item"
                :class="child.variant ? `variant-${child.variant}` : ''"
                @click="handleChildClick(child)"
              >
                <span class="item-icon-wrap">
                  <font-awesome-icon class="item-icon" :icon="child.icon" />
                </span>
                <span>{{ child.label }}</span>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Expanded children (inline) -->
    <transition name="expand">
      <ul v-if="isExpanded && isOpen" class="children-list">
        <li v-for="child in children" :key="child.route || child.label" class="child-item-wrapper">
          <router-link
            v-if="child.route"
            :to="child.route"
            class="sidebar-nav child-nav"
            :class="[{ 'active': isChildActive(child.route), 'locked': child.lock }, child.variant ? `variant-${child.variant}` : '']"
            active-class=""
            exact-active-class=""
            :data-guide="child.guide"
            @click="child.action && child.action()"
          >
            <span class="icon-wrap">
              <font-awesome-icon class="icon" :icon="child.icon" />
              <span v-if="child.badge" class="child-badge"></span>
              <font-awesome-icon v-if="child.lock" class="child-lock-icon" :icon="['fas', 'lock']" />
            </span>
            <span class="nav-text">{{ child.label }}</span>
          </router-link>
          <div
            v-else
            class="sidebar-nav child-nav"
            :class="child.variant ? `variant-${child.variant}` : ''"
            @click="handleChildClick(child)"
          >
            <span class="icon-wrap">
              <font-awesome-icon class="icon" :icon="child.icon" />
            </span>
            <span class="nav-text">{{ child.label }}</span>
          </div>
        </li>
      </ul>
    </transition>
  </li>
</template>

<style scoped>
/* Base styles - must replicate Sidebar.vue base styles */

.expandable-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

/* All idle/hover/active/disabled colors are themeable via --sidebar-* custom
   properties set by the hosting product sidebar (see webscout Sidebar.vue's
   unscoped block); every fallback preserves the legacy look for products that
   set nothing (ja4db). */
.sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: var(--sidebar-text, var(--text-secondary));
  display: flex;
  align-items: center;
  transition: var(--transition);
  border-radius: var(--radius-lg);
  position: relative;
  gap: 0;
  cursor: pointer;
}

.sidebar-nav:hover {
  color: var(--sidebar-hover-text, var(--text-heading));
  background-color: var(--sidebar-hover-bg, var(--surface-hover));
}

/* Parent active state (child active, or dropdown open in collapsed mode) */
.sidebar-nav.active {
  background-color: var(--sidebar-parent-active-bg, var(--surface-hover));
  color: var(--sidebar-parent-active-color, var(--text-heading)) !important;
}

/* Icon base styles */
.sidebar-nav .icon {
  transition: margin-right 0.3s ease;
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Chevron positioning */
.chevron {
  margin-left: auto;
  font-size: 0.85rem;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  color: var(--sidebar-chevron-color, currentColor);
}

/* Text visibility base */
.nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

/* Children list — top padding makes the parent → first-child gap match the
   0.5rem gap between sibling children */
.children-list {
  list-style: none;
  padding: 0.25rem 0 0;
  margin: 0;
  overflow: hidden;
}

.child-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
  padding-left: 0;
}

/* Child nav items - match exact sidebar styling */
.child-nav {
  padding-left: 2rem !important;
  color: var(--sidebar-text, var(--text-secondary));
  text-decoration: none;
}

.child-nav:hover {
  color: var(--sidebar-hover-text, var(--text-heading));
  background-color: var(--sidebar-hover-bg, var(--surface-hover));
}

.child-nav.active {
  background-color: var(--sidebar-active-bg, var(--surface-hover));
  color: var(--sidebar-active-color, var(--text-heading)) !important;
  font-weight: var(--sidebar-active-weight, inherit);
}

/* Tier-locked children fade as a whole row but stay clickable */
.child-nav.locked,
.dropdown-item.locked {
  opacity: var(--sidebar-disabled-opacity, 1);
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: var(--transition-slow);
  max-height: 500px;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Dropdown menu for collapsed sidebar */
.expandable-dropdown-menu {
  background: var(--sidebar-menu-bg, var(--surface-page));
  border: 1px solid var(--sidebar-menu-border, var(--border));
  border-radius: var(--radius-lg);
  box-shadow: var(--sidebar-menu-shadow, 0 10px 40px rgba(0, 0, 0, 0.4));
  z-index: 9999;
  min-width: 200px;
}

/* Bridge covering the gap between the rail and the menu: keeps the pointer
   "inside" the dropdown while crossing, and (when themed) visually connects
   the menu to the rail. Spans only the trigger button's row (height/alignment
   set by updateMenuPosition), and overlaps 1rem into the menu so its rounded
   left corners can't show a seam; transparent unless the product sets
   --sidebar-menu-bridge-bg. */
.expandable-dropdown-menu::before {
  content: '';
  position: absolute;
  height: var(--sidebar-bridge-height, 3rem);
  /* 16px gap + 20px into the trigger pill (past its 12px corner radius, so
     the pill's rounded corners are fully covered) + 12px into the menu (past
     the menu's own corner radius, but short of the first item's hover pill).
     All overlap lands on same-colored surfaces. */
  left: -36px;
  width: 48px;
  background: var(--sidebar-menu-bridge-bg, transparent);
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
  /* ::before positions against the padding box; the menu's 1px border shifts
     that 1px inward, so pull back out to line up with the trigger row. */
  top: -1px;
}

.expandable-dropdown-menu.align-bottom::before {
  top: auto;
  bottom: -1px;
}

/* Collapsed dropdown header — names the group the items belong to. Left
   padding lines up with the item labels' icon column. */
.dropdown-header {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 0.85rem 1.25rem 0;
  white-space: nowrap;
  /* Paint above the ::before gap bridge, like .dropdown-items */
  position: relative;
}

.dropdown-items {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  /* Paint above the ::before gap bridge so item hover pills keep their
     rounded left corners */
  position: relative;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  color: var(--sidebar-text, var(--text-primary));
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
  text-decoration: none;
  cursor: pointer;
  font-size: var(--sidebar-menu-font-size, 0.85rem);
}

.dropdown-item:hover {
  background-color: var(--sidebar-hover-bg, var(--accent-soft-3));
  color: var(--sidebar-hover-text, var(--text-heading));
}

.dropdown-item.active {
  background-color: var(--sidebar-active-bg, rgba(115, 87, 210, 0.2));
  color: var(--sidebar-active-color, var(--text-heading));
  font-weight: var(--sidebar-active-weight, inherit);
}

.dropdown-item .item-icon {
  width: 1.2rem;
  font-size: 1rem;
  flex-shrink: 0;
  color: var(--sidebar-text, var(--text-secondary))
}

.dropdown-item:hover .item-icon {
  color: var(--sidebar-hover-text, var(--text-secondary));
}

.dropdown-item.active .item-icon {
  color: var(--sidebar-active-color, var(--text-secondary));
}

/* Icon wrap for badge positioning */
.icon-wrap,
.item-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-wrap .icon {
  margin-right: 0 !important;
}

/* Lock badge for tier-restricted children (mirrors Sidebar's .nav-lock-icon) */
.child-lock-icon {
  position: absolute;
  top: -3px;
  right: -5px;
  font-size: 0.35rem;
  color: var(--text-muted);
  background: var(--sidebar-badge-ring, var(--surface-raised));
  border-radius: var(--radius-pill);
  width: 10px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Child / parent badge (red dot for unread) */
.child-badge,
.parent-badge {
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  background: var(--color-error);
  border-radius: var(--radius-pill);
  border: 2px solid var(--sidebar-badge-ring, var(--surface-card));
  animation: sidebar-pulse-badge 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  pointer-events: none;
}

.dropdown-item .child-badge {
  border-color: var(--sidebar-menu-bg, var(--surface-page));
}

.parent-badge.inline {
  position: static;
  margin-left: 0.5rem;
  border-color: var(--sidebar-badge-ring, var(--surface-card));
}

@keyframes sidebar-pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Danger variant (logout) */
.child-nav.variant-danger {
  color: var(--color-error) !important;
}

.child-nav.variant-danger:hover {
  color: var(--color-error-text) !important;
}

.dropdown-item.variant-danger {
  color: var(--color-error) !important;
}

.dropdown-item.variant-danger .item-icon {
  color: var(--color-error);
}

.dropdown-item.variant-danger:hover {
  color: var(--color-error-text) !important;
  background-color: rgba(202, 80, 80, 0.12);
}

/* Dropdown animation */
.expandable-dropdown-enter-active,
.expandable-dropdown-leave-active {
  transition: var(--transition);
}

.expandable-dropdown-enter-from,
.expandable-dropdown-leave-to {
  opacity: 0;
  transform: translateX(8px);
}
</style>

<!-- Unscoped styles for handling collapsed/expanded sidebar states -->
<style>
/* Collapsed sidebar - center icon, hide text */
#sidebar:not(.expanded) .expandable-wrapper .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#sidebar:not(.expanded) .expandable-wrapper .nav-text {
  display: none;
}

/* Expanded sidebar - show text with margin */
#sidebar.expanded .expandable-wrapper .sidebar-nav .icon-wrap {
  margin-right: 0.75rem;
}

#sidebar.expanded .expandable-wrapper .nav-text {
  display: inline;
}
</style>
